import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../PageLayout';
import { colors, typography } from '../../theme/colors';
import { buildCycleGraph, filterGraph, resolveUserNames } from '../../utils/cycleReportHelper';
import CycleCanvas from './CycleCanvas';
import CycleKPIStrip from './CycleKPIStrip';
import CycleSidePanel from './CycleSidePanel';
import CycleAuditPopover from './CycleAuditPopover';
import CycleTimelineRibbon from './CycleTimelineRibbon';
import CycleStuckList from './CycleStuckList';

// Shared shell for the Purchase Cycle and Sale Cycle reports. The two pages
// pass in their own lane config and party-master loader.
//
// Read-only by design — touches no transaction writes. Every doc on the
// canvas links back to its source page via existing navigateToTransaction.
const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const CycleReportShell = ({
  title,
  lanes,
  partyType,           // 'vendor' | 'customer'
  partyCollectionName, // 'vendors' | 'customers'
  partyNameField,      // 'vendorName' | 'companyName'
  defaultStaleAfterDays = 30
}) => {
  const { activeCompany, activeBranch } = useAuth();
  const currencyCode = activeCompany?.currencyCode || 'PKR';

  // Filters
  const [fromDate, setFromDate] = useState(daysAgo(60));
  const [toDate, setToDate] = useState(today());
  const [docNumberFilter, setDocNumberFilter] = useState('');
  const [partyFilter, setPartyFilter] = useState('');
  const [itemFilter, setItemFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Reference data for filter dropdowns. Loaded once.
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);

  // Graph state
  const [loading, setLoading] = useState(false);
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(null);
  const [userMap, setUserMap] = useState({});

  // UI state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [highlightedChainNodeIds, setHighlightedChainNodeIds] = useState([]);
  const [selectedChain, setSelectedChain] = useState(null);
  const [auditState, setAuditState] = useState({ node: null, anchor: null });

  // Load filter masters
  useEffect(() => {
    if (!activeCompany?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [pSnap, iSnap] = await Promise.all([
          getDocs(query(collection(db, 'companies', activeCompany.id, partyCollectionName), orderBy(partyNameField, 'asc'))),
          getDocs(query(collection(db, 'companies', activeCompany.id, 'inventoryItems'), orderBy('itemName', 'asc')))
        ]);
        if (cancelled) return;
        setParties(pSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.isActive !== false));
        setItems(iSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));
      } catch (err) {
        console.error('[CycleReport] master load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCompany, partyCollectionName, partyNameField]);

  // Load graph whenever date range or branch changes (heavy, hits Firestore).
  // Filters that work on already-loaded data (docNumber/party/item/status)
  // are applied via filterGraph below — they don't refetch.
  const loadGraph = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      setError(null);
      const branchId = activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null;
      const g = await buildCycleGraph(activeCompany.id, {
        lanes,
        fromDate,
        toDate,
        branchId,
        staleAfterDays: defaultStaleAfterDays
      });
      setGraph(g);
      // Resolve user names for the audit popover (collected lazily — only
      // uids that appear in nodes).
      const uids = new Set();
      g.nodes.forEach(n => { if (n.createdBy) uids.add(n.createdBy); if (n.postedBy) uids.add(n.postedBy); if (n.updatedBy) uids.add(n.updatedBy); });
      if (uids.size > 0) {
        const map = await resolveUserNames(activeCompany.id, [...uids]);
        setUserMap(map);
      } else {
        setUserMap({});
      }
    } catch (err) {
      console.error('[CycleReport] graph load failed:', err);
      setError(err?.message || 'Failed to load cycle data');
      setGraph(null);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeBranch, lanes, fromDate, toDate, defaultStaleAfterDays]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  // Apply in-memory filters (no refetch).
  const filteredGraph = useMemo(() => {
    if (!graph) return null;
    return filterGraph(graph, {
      docNumber: docNumberFilter,
      partyId: partyFilter || null,
      itemId: itemFilter || null,
      status: statusFilter || null
    });
  }, [graph, docNumberFilter, partyFilter, itemFilter, statusFilter]);

  // When filters change such that the currently selected chain or node is no
  // longer visible, clear the selection so the side panel and ribbon don't
  // render stale data.
  useEffect(() => {
    if (!filteredGraph) return;
    const visibleIds = new Set(filteredGraph.nodes.map(n => n.id));
    if (selectedNodeId && !visibleIds.has(selectedNodeId)) {
      setSelectedNodeId(null);
      setSelectedChain(null);
      setHighlightedChainNodeIds([]);
      setAuditState({ node: null, anchor: null });
    }
  }, [filteredGraph, selectedNodeId]);

  // When the user clicks a node, light up its entire chain.
  const onNodeClick = useCallback((node) => {
    if (!filteredGraph) return;
    setSelectedNodeId(node.id);
    const chain = filteredGraph.chains.find(c => c.nodeIds.includes(node.id));
    setSelectedChain(chain || null);
    setHighlightedChainNodeIds(chain ? chain.nodeIds : [node.id]);
  }, [filteredGraph]);

  const onBackgroundClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedChain(null);
    setHighlightedChainNodeIds([]);
    setAuditState({ node: null, anchor: null });
  }, []);

  const onNodeAuditClick = useCallback((node, anchorEl) => {
    setAuditState({ node, anchor: anchorEl });
  }, []);

  const onSelectChain = useCallback((chain) => {
    if (!filteredGraph) return;
    setSelectedChain(chain);
    const rootNode = filteredGraph.nodes.find(n => n.id === chain.rootId);
    setSelectedNodeId(rootNode?.id || null);
    setHighlightedChainNodeIds(chain.nodeIds);
  }, [filteredGraph]);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId || !filteredGraph) return null;
    return filteredGraph.nodes.find(n => n.id === selectedNodeId) || null;
  }, [selectedNodeId, filteredGraph]);

  const resetFilters = () => {
    setDocNumberFilter('');
    setPartyFilter('');
    setItemFilter('');
    setStatusFilter('');
    setFromDate(daysAgo(60));
    setToDate(today());
  };

  return (
    <PageLayout title={title}>
      <Page>
        <FilterBar>
          <FilterGroup>
            <Label>From</Label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To</Label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </FilterGroup>
          <FilterGroup style={{ minWidth: 160 }}>
            <Label>Doc #</Label>
            <Input
              type="text"
              placeholder="e.g. PO-2024-001"
              value={docNumberFilter}
              onChange={e => setDocNumberFilter(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup style={{ minWidth: 160 }}>
            <Label>{partyType === 'vendor' ? 'Vendor' : 'Customer'}</Label>
            <Select value={partyFilter} onChange={e => setPartyFilter(e.target.value)}>
              <option value="">All</option>
              {parties.map(p => (
                <option key={p.id} value={p.id}>{p[partyNameField] || ''}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 160 }}>
            <Label>Item</Label>
            <Select value={itemFilter} onChange={e => setItemFilter(e.target.value)}>
              <option value="">All</option>
              {items.map(i => (
                <option key={i.id} value={i.id}>{i.itemName}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 130 }}>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option>Draft</option>
              <option>Posted</option>
              <option>Approved</option>
              <option>Partial</option>
              <option>Paid</option>
              <option>Cancelled</option>
            </Select>
          </FilterGroup>
          <ResetBtn onClick={resetFilters} title="Reset filters">Reset</ResetBtn>
        </FilterBar>

        {loading ? (
          <Center>Loading cycle data…</Center>
        ) : error ? (
          <Error>{error}</Error>
        ) : (
          <>
            <CycleKPIStrip
              stats={filteredGraph?.stats}
              lanes={lanes}
              currencyCode={currencyCode}
            />

            {selectedChain && (
              <CycleTimelineRibbon
                chain={selectedChain}
                nodes={filteredGraph?.nodes || []}
              />
            )}

            <CanvasRow>
              <CanvasArea>
                <CycleCanvas
                  graph={filteredGraph}
                  selectedNodeId={selectedNodeId}
                  highlightedChainNodeIds={highlightedChainNodeIds}
                  onNodeClick={onNodeClick}
                  onNodeAuditClick={onNodeAuditClick}
                  onBackgroundClick={onBackgroundClick}
                />
              </CanvasArea>
              {selectedNode && (
                <CycleSidePanel
                  node={selectedNode}
                  onClose={() => onBackgroundClick()}
                  currencyCode={currencyCode}
                />
              )}
            </CanvasRow>

            <CycleStuckList
              stuck={filteredGraph?.stuck || []}
              onSelectChain={onSelectChain}
              staleAfterDays={defaultStaleAfterDays}
            />
          </>
        )}

        {auditState.node && auditState.anchor && (
          <CycleAuditPopover
            node={auditState.node}
            anchor={auditState.anchor}
            userMap={userMap}
            onClose={() => setAuditState({ node: null, anchor: null })}
          />
        )}
      </Page>
    </PageLayout>
  );
};

export default CycleReportShell;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
`;
const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  flex-wrap: wrap;
`;
const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
const Label = styled.label`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
`;
const Input = styled.input`
  padding: 7px 10px;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const Select = styled.select`
  padding: 7px 10px;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  background: white;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const ResetBtn = styled.button`
  padding: 7px 14px;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  background: white;
  color: ${colors.text.primary};
  font-size: 13px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  margin-left: auto;
  &:hover { color: ${colors.primary.main}; border-color: ${colors.primary.main}; }
`;
const CanvasRow = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  min-height: 480px;
`;
const CanvasArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
`;
const Center = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: ${colors.text.primary};
  font-size: 14px;
  font-weight: 500;
`;
const Error = styled.div`
  padding: 14px 16px;
  background: ${colors.error.main}10;
  border: 1px solid ${colors.error.main};
  border-radius: 6px;
  color: ${colors.error.main};
  font-size: 13px;
  font-weight: 500;
`;
